import { ApolloClient, ApolloLink } from '@apollo/client';
import { get } from 'lodash';
import { setContext } from '@apollo/client/link/context';
import fetch from 'isomorphic-unfetch';
import getConfig from 'next/config';
import { getDefaultLocale } from '../locales';
import { cache } from './cache';
import { getSplitLink } from './link';
import { getAuthToken } from '../hocs/withAuth/service';

let apolloClient = null;

// Pull related configuration variables from the runtime config
const {
  publicRuntimeConfig: { LODGELINK_API_URI, LODGELINK_ADMIN_BFF_URI, PLATFORM_KEY }
} = getConfig();

// Polyfill fetch() on the server (used by apollo-client)
if (!process.browser) {
  global.fetch = fetch;
}

function create(initialState, { locale = getDefaultLocale(), analyticIDs = {} }) {
  /**
   * Separates the API calls to the Admin BFF and the API for the Margins Page.
   */
  const splitLink = getSplitLink(LODGELINK_API_URI, `${LODGELINK_ADMIN_BFF_URI}/graphql`);

  /**
   * Middleware for the HTTPLink to append headers to all requests.
   */
  const augmentedHeaders = setContext(async (_, { headers }) => {
    /**
     * Auth Headers.
     *
     * Used by the API for access control and identification.
     */
    const authHeaders = {};

    try {
      const accessToken = await getAuthToken();
      if (accessToken) {
        authHeaders.authorization = `Bearer ${accessToken}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }

    /**
     * Analytics Headers.
     *
     * Used to relate/connect analytics captured on the front end with the analytics captured
     * on the back end.
     *
     * Mainly helps with tracking of unauthenticated users.
     */
    const analyticsHeaders = {
      clientId: analyticIDs.LLV2_CLIENT_ID,
      sessionId: analyticIDs.LLV2_SESSION_ID
    };

    /**
     * Locale Headers.
     *
     * Tells the API how it should localize its responses.
     * Setting a default value now because it allows us to test things easier, and might prevent some bad stuff if we make a local setting issue.
     */
    const localeHeaders = {
      locale: get(locale, 'id', 'en')
    };

    /**
     * Adding platform key header
     */
    const environmentHeaders = {
      platform: PLATFORM_KEY,
      runtime: process.browser ? 'BROWSER' : 'NODEJS'
    };

    return {
      headers: {
        ...headers,
        ...authHeaders,
        ...analyticsHeaders,
        ...localeHeaders,
        ...environmentHeaders
      }
    };
  });

  /**
   * Create a cache.
   *
   * We can also define what the unique identifier for each type is
   * this should only be used when the default "id" is not found on a type.
   */
  const apolloCache = cache.restore(initialState || {});

  const links = ApolloLink.from([augmentedHeaders, splitLink]);

  return new ApolloClient({
    devtools: {
      enabled: process.browser
    },
    ssrMode: !process.browser, // Disables forceFetch on the server (so queries are only run once)
    link: links,
    cache: apolloCache
  });
}

function initApollo(initialState = {}, options = {}, recreate = false) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections
  if (!process.browser) {
    return create(initialState, { ...options });
  }

  // Reuse client on the client-side, so we aren't creating extra overhead.
  if (!apolloClient || recreate) {
    apolloClient = create(initialState, { ...options });
  }

  return apolloClient;
}

export default initApollo;

export const getClient = () => apolloClient;
